/**
 * rtRegisterModule.js — обвязка Этапа 3: жёсткая регистрация КТ↔МРТ + наложение.
 *
 * Ядро (rigidRegister.js) — чистая математика, проверено автономными тестами
 * (осеориент. и косая геометрия, суб-воксель; наложение геометрией; матрица 4×4).
 * Здесь — связь с Cornerstone: достать воксели двух томов, зарегистрировать,
 * применить к геометрии подвижного тома, показать его цветным полупрозрачным
 * слоем поверх опорного и дать ручную докрутку.
 *
 * ПРОВЕРКА: расчёт и перестановка геометрии проверены. Извлечение вокселей,
 * рендер наложения и цветовую карту проверить можно только вживую — код
 * защищён (try/catch, журнал `[REG] …`).
 */

import { cornerstone, getRenderingEngine } from '../core/viewportState.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { log } from '../utils/logger.js';
import { fusionColorPoints, fusionOpacityPoints } from './fusionStyleCore.js';
import {
  registerRigid, applyRigidToGeometry, volumeCenterWorld, paramsToMatrix4, cropVolumeToBox, worldToIdx,
} from './rigidRegister.js';
import { growLesion } from '../core/lesionVolume.js';   // [10.14.0] объём очага
import { getComparePair } from '../core/compareState.js';   // [10.8.0] какую пару совмещать — решает врач в разделе «Сравнение»

// Текущее совмещение (для ручной докрутки и наложения).
// Исходный кадр отсчёта подвижного тома — чтобы вернуть его при снятии
// совмещения: мы переписываем кадр в памяти и обязаны уметь это отменить.
const _origFrameUID = new Map();
let _state = null; // { movingId, origGeom, center, params:[6], autoParams:[6], opacity }

/** Метаданные тома для новой серии (пациент, кадр отсчёта, описание). */
function _volMeta(csVol) {
  const id = (csVol.imageIds || [])[0];
  const out = { patientName: '', patientId: '', forUID: null, seriesDesc: '', studyUID: null, studyDesc: '' };
  try { const p = cornerstone.metaData.get('patientModule', id); const pn = p?.patientName; out.patientName = (pn && pn.Alphabetic) || pn || ''; out.patientId = p?.patientId || ''; } catch (e) { /* */ }
  try { const pl = cornerstone.metaData.get('imagePlaneModule', id); out.forUID = pl?.frameOfReferenceUID || null; } catch (e) { /* */ }
  try { const s = cornerstone.metaData.get('generalSeriesModule', id); out.seriesDesc = s?.seriesDescription || ''; } catch (e) { /* */ }
  // [7.0.31] Исследование источника. Раньше его не спрашивали — и вырезанная серия
  // уходила в НОВОЕ исследование, унаследовав при этом FrameOfReferenceUID
  // источника. Стандарт (PS3.3 C.7.4.1) допускает общий кадр отсчёта у двух серий
  // ОДНОГО исследования; через границу исследований такая связь не определена, и
  // системы, которые опираются на кадр отсчёта для пространственной привязки,
  // её обычно просто не увидят — то есть терялось ровно то, ради чего кадр и
  // наследовался. Производная серия должна лежать в исследовании источника.
  try {
    const st = cornerstone.metaData.get('generalStudyModule', id);
    out.studyUID = st?.studyInstanceUID || null;
    out.studyDesc = st?.studyDescription || '';
  } catch (e) { /* */ }
  return out;
}

/**
 * [7.0.29] Вырезать «КТ головы» в ОТДЕЛЬНУЮ ВИДИМУЮ СЕРИЮ. В отличие от галочки
 * (та обрезает лишь ради расчёта совмещения), здесь получается настоящая серия
 * DICOM: она появляется в списке, листается во всех окнах, сохраняется/
 * выгружается. Кадр отсчёта наследуется от источника — обрезок в тех же
 * координатах, ложится поверх оригинала и штатно совмещается с МРТ.
 * @returns {Promise<{slices:number, keptFraction:number, dims:number[]}>}
 */
export async function extractHeadCT(opts = {}) {
  const vols = _loadedVolumes();
  if (!vols.length) throw new Error('Нет загруженной серии.');
  const bySize = (a, b) => {
    const da = a.dimensions || a.imageData?.getDimensions?.() || [0, 0, 0];
    const db = b.dimensions || b.imageData?.getDimensions?.() || [0, 0, 0];
    return db[0] * db[1] * db[2] - da[0] * da[1] * da[2];
  };
  // источник: явный → КТ → самый большой том
  const src = (opts.volumeId && _volById(opts.volumeId))
    || vols.find(v => _modalityOf(v) === 'CT') || [...vols].sort(bySize)[0];
  if (!src) throw new Error('Не нашёл том-источник.');

  const center = opts.center || _focalPointWorld();
  if (!center) throw new Error('Нет перекрестья — наведите его на голову в КТ.');
  const halfMm = Math.max(40, Math.min(200, +opts.halfMm || 110));

  const full = extractVolume(src, 1, { noCopy: true });   // без удвоения огромного тома в памяти
  if (!full) throw new Error('Нет доступа к вокселям тома.');
  const crop = cropVolumeToBox(full, center, halfMm);      // копирует только маленький обрезок

  const meta = _volMeta(src);
  const { buildCTSeriesDicomFiles } = await import('../io/ctSeriesCore.js');
  const seed = String(Date.now()).replace(/[^0-9]/g, '').slice(-11) || '9000000001';
  const specs = buildCTSeriesDicomFiles(crop, {
    uidSeed: seed,
    patientName: meta.patientName || 'UNKNOWN^PATIENT',
    patientId: meta.patientId || '000000',
    frameOfReferenceUID: meta.forUID || undefined,      // ← та же система координат, что у источника
    studyUID: meta.studyUID || undefined,               // ← и то же исследование, иначе кадр отсчёта «висит»
    seriesDescription: `КТ головы (обрезка ${Math.round(halfMm * 2)} мм)`,
    // StudyDescription — описание ИССЛЕДОВАНИЯ, а не серии: раньше сюда клалось
    // seriesDesc источника, и серия подписывалась чужим по смыслу полем.
    studyDescription: meta.studyDesc || '',
  });
  if (!specs.length) throw new Error('Обрезка дала пустой объём — сдвиньте перекрестье на голову.');

  const files = specs.map(f => new File([f.bytes], f.name, { type: 'application/dicom' }));
  const { handleFiles } = await import('../ui/fileOps.js');
  await handleFiles(files);
  log.info(`[REG] КТ головы вырезана в новую серию: ${files.length} срезов ${crop.dims.join('×')}, `
    + `оставлено ${(crop.keptFraction * 100).toFixed(1)}%, FoR ${meta.forUID || 'новый'}.`);
  return { slices: files.length, keptFraction: crop.keptFraction, dims: crop.dims };
}

/** Мировая точка перекрестья (фокус камеры первого MPR-окна) — центр обрезки. */
function _focalPointWorld() {
  try {
    const vp = (getRenderingEngine()?.getViewports?.() || [])
      .find(v => String(v?.type || '').toLowerCase().includes('orthographic'));
    const fp = vp?.getCamera?.()?.focalPoint;
    if (fp && fp.length === 3 && fp.every(Number.isFinite)) return [fp[0], fp[1], fp[2]];
  } catch (e) { /* */ }
  return null;
}

// [6.7.37] Исходная («как загружено») геометрия каждого тома. Раньше её брали
// из тома в момент вызова — а он к этому времени мог быть уже сдвинут прошлым
// совмещением. Загрузка сохранённого совмещения поверх уже применённого
// накладывала преобразование ВТОРОЙ раз и уводила МРТ вдвое дальше цели.
const _pristine = new Map();   // volumeId → { origin, direction }
function _pristineGeom(vol, id) {
  let g = _pristine.get(id);
  if (!g) {
    const cur = _geomOf(vol);
    g = {
      origin: cur.origin ? Array.from(cur.origin) : [0, 0, 0],
      direction: cur.direction ? Array.from(cur.direction) : null,
    };
    _pristine.set(id, g);
  }
  return { origin: g.origin.slice(), direction: g.direction ? g.direction.slice() : null };
}

// ── извлечение вокселей и геометрии тома Cornerstone ─────────────────────────

function _scalarsOf(v) {
  let sd = null;
  try { sd = v.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { /* */ }
  if (!sd) { try { sd = v.getScalarData?.(); } catch (e) { /* */ } }
  if (!sd) { try { sd = v.scalarData; } catch (e) { /* */ } }
  if (!sd) { try { sd = v.imageData?.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { /* */ } }
  return (sd && sd.length) ? sd : null;
}
function _geomOf(v) {
  const img = v.imageData;
  const dims = v.dimensions || img?.getDimensions?.();
  const spacing = v.spacing || img?.getSpacing?.();
  const origin = v.origin || img?.getOrigin?.();
  let direction = v.direction || img?.getDirection?.();
  if (direction && direction.length !== 9) direction = null;
  return {
    dims: dims && Array.from(dims), spacing: spacing && Array.from(spacing),
    origin: origin && Array.from(origin), direction: direction && Array.from(direction),
  };
}

/** Плоский объём {data,dims,spacing,origin,direction}; step>1 — прореживание. */
export function extractVolume(csVol, step = 1, opts = {}) {
  const sd = _scalarsOf(csVol);
  const g = _geomOf(csVol);
  if (!sd || !g.dims || g.dims.length !== 3) return null;
  const [nx, ny, nz] = g.dims;
  const spacing = g.spacing || [1, 1, 1];
  const origin = g.origin || [0, 0, 0];
  const direction = g.direction || null;
  if (step <= 1) {
    // [7.0.29] noCopy: не удваивать огромный том в памяти (КТ всего тела —
    // сотни МБ). Потребитель, который только ВЫРЕЗАЕТ малый кусок, читает
    // исходный типизированный массив как есть.
    return {
      data: (opts.noCopy || sd instanceof Float32Array) ? sd : Float32Array.from(sd),
      dims: [nx, ny, nz], spacing, origin, direction,
    };
  }
  const mx = Math.floor((nx - 1) / step) + 1;
  const my = Math.floor((ny - 1) / step) + 1;
  const mz = Math.floor((nz - 1) / step) + 1;
  const out = new Float32Array(mx * my * mz);
  let p = 0;
  for (let k = 0; k < mz; k++) {
    const kk = k * step;
    for (let j = 0; j < my; j++) {
      const base = nx * (j * step + ny * kk);
      for (let i = 0; i < mx; i++) out[p++] = sd[base + i * step];
    }
  }
  return {
    data: out, dims: [mx, my, mz],
    spacing: [spacing[0] * step, spacing[1] * step, spacing[2] * step],
    origin, direction,
  };
}

function _modalityOf(csVol) {
  try {
    const id = csVol.imageIds && csVol.imageIds[0];
    if (id && cornerstone?.metaData) {
      const m = cornerstone.metaData.get('generalSeriesModule', id);
      if (m?.modality) return String(m.modality).toUpperCase();
    }
  } catch (e) { /* */ }
  return '';
}
function _seriesUidOf(csVol) {
  try {
    const id = csVol.imageIds && csVol.imageIds[0];
    const m = id && cornerstone?.metaData?.get('generalSeriesModule', id);
    return m?.seriesInstanceUID || null;
  } catch (e) { return null; }
}
function _loadedVolumes() {
  try {
    return (cornerstone?.cache?.getVolumes?.() || []).filter(v => {
      const d = v.dimensions || v.imageData?.getDimensions?.();
      return d && d.length === 3 && d[2] > 1;
    });
  } catch (e) { return []; }
}
function _volById(id) {
  return _loadedVolumes().find(v => (v.volumeId || v.id) === id) || null;
}
function _mprViewports() {
  try {
    const re = getRenderingEngine();
    return (re?.getViewports?.() || []).filter(vp =>
      String(vp?.type || '').toLowerCase().includes('orthographic'));
  } catch (e) { return []; }
}
function _scalarRange(csVol) {
  try {
    const r = csVol.imageData?.getPointData?.()?.getScalars?.()?.getRange?.();
    if (r && r.length === 2 && r[1] > r[0]) return r;
  } catch (e) { /* */ }
  const sd = _scalarsOf(csVol);
  if (!sd) return [0, 1];
  let mn = Infinity, mx = -Infinity;
  const stp = Math.max(1, (sd.length / 20000) | 0);
  for (let i = 0; i < sd.length; i += stp) { const v = sd[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
  return [mn, mx];
}

/** Сколько вокселей оставляем после прореживания — см. пояснение у step. */
const ЦЕЛЬ_ВОКСЕЛЕЙ = 400000;

function _шагПрореживания(csVol) {
  try {
    const d = csVol?.dimensions || csVol?.imageData?.getDimensions?.();
    if (!d || d.length !== 3) return 3;
    const n = d[0] * d[1] * d[2];
    if (!Number.isFinite(n) || n <= 0) return 3;
    return Math.max(1, Math.ceil(Math.cbrt(n / ЦЕЛЬ_ВОКСЕЛЕЙ)));
  } catch (e) { return 3; }
}

// ── главная операция ────────────────────────────────────────────────────────

/**
 * Совместить КТ и МРТ. По умолчанию опорный (fixed) — КТ, подвижный — МРТ.
 * @param {{fixedId?,movingId?,step?,bins?,apply?,fuse?,opacity?}} [opts]
 */
export async function registerCtMr(opts = {}) {
  const vols = _loadedVolumes();
  if (vols.length < 2) { log.warn('[REG] Нужны два тома (КТ и МРТ). Найдено:', vols.length); return null; }

  let fixedVol, movingVol;
  if (opts.fixedId || opts.movingId) {
    fixedVol = _volById(opts.fixedId) || vols[0];
    movingVol = _volById(opts.movingId) || vols.find(v => v !== fixedVol);
  } else {
    const ct = vols.find(v => _modalityOf(v) === 'CT');
    const mr = vols.find(v => ['MR', 'MRI'].includes(_modalityOf(v)));
    if (ct && mr) { fixedVol = ct; movingVol = mr; }
    else {
      const s = [...vols].sort((a, b) => {
        const da = a.dimensions || [0, 0, 0], db = b.dimensions || [0, 0, 0];
        return db[0] * db[1] * db[2] - da[0] * da[1] * da[2];
      });
      fixedVol = s[0]; movingVol = s[1];
    }
  }
  if (!fixedVol || !movingVol || fixedVol === movingVol) { log.warn('[REG] Не выбрать пару.'); return null; }

  const movingId = movingVol.volumeId || movingVol.id;
  const fixedId = fixedVol.volumeId || fixedVol.id;
  // Геометрию «как загружено» фиксируем ДО любых перестановок.
  const orig = _pristineGeom(movingVol, movingId);

  // [10.8.0] Шаг прореживания — по размеру тома, а не «всегда третий воксель».
  //
  // Тройка ставилась под настоящую КТ 512×512×200: там она оставляет около
  // трёхсот тысяч вокселей, чего взаимной информации с запасом хватает. Но на
  // небольшом томе та же тройка оставляет несколько тысяч — и совмещение
  // выдаёт уверенный ответ по шуму. Поймано на проверке: на голове 72×72×26
  // возвращался сдвиг 0,4 мм вместо 6 при MI = 0,9. Ошибки нет, число есть,
  // число неверное — худший род поломки.
  const step = opts.step || _шагПрореживания(fixedVol);
  let fixed = extractVolume(fixedVol, step);
  const moving = extractVolume(movingVol, step);
  if (!fixed || !moving) { log.warn('[REG] Нет доступа к вокселям тома.'); return null; }
  // [10.8.0] Размеры обоих томов — в журнал. Совмещение целиком зависит от
  // того, ЧТО ему дали: если в томе оказалось четыре среза вместо тридцати
  // четырёх, ответ будет уверенным и неверным, а по MI этого не видно.
  log.info(`[REG] опорный ${fixed.dims.join('×')} шаг ${fixed.spacing.map(v => v.toFixed(2)).join('/')} начало ${fixed.origin.map(v => v.toFixed(1)).join('/')}; `
    + `подвижный ${moving.dims.join('×')} шаг ${moving.spacing.map(v => v.toFixed(2)).join('/')} начало ${moving.origin.map(v => v.toFixed(1)).join('/')}; прореживание ${step}`);

  // [7.0.29] Обрезка опорного тома до головы. Если КТ — всего тела, её центр в
  // животе, и свести его с МРТ головы нечем (даже затравка по центрам тянет
  // живот к голове). Обрезав КТ до бокса ±halfMm вокруг заданной точки (обычно
  // перекрестье, наведённое врачом на голову), получаем совпадающие центры и
  // рабочий захват. Точка — в мировых координатах опорного тома.
  let cropInfo = null;
  if (opts.cropFixedTo && Number.isFinite(opts.cropFixedTo.halfMm)) {
    try {
      const c = cropVolumeToBox(fixed, opts.cropFixedTo.center, opts.cropFixedTo.halfMm);
      if (c.cropped) { fixed = c; cropInfo = { keptFraction: c.keptFraction, dims: c.dims }; }
      log.info(`[REG] Опорный том обрезан до головы: оставлено ${(c.keptFraction * 100).toFixed(1)}% (${c.dims.join('×')}).`);
    } catch (e) {
      log.warn('[REG] Обрезка не удалась:', e.message);
      return { ok: false, mi: 0, overlap: 0, params: [0, 0, 0, 0, 0, 0], fixedId, movingId,
        reason: 'Обрезка вне тома: наведите перекрестье на голову в КТ и повторите.' };
    }
  }

  // Считаем от исходной геометрии подвижного тома, а не от текущей: иначе
  // повторный запуск «Совместить» стартовал бы из уже сдвинутого положения.
  const movingFromOrig = { ...moving, origin: orig.origin, direction: orig.direction };

  // step не навязываем — registerRigid сам подберёт прореживание под бюджет выборок
  const t0 = Date.now();
  const res = registerRigid(fixed, movingFromOrig, { bins: opts.bins || 32, maxSamples: opts.maxSamples });
  const DEG = 180 / Math.PI;
  log.info(`[REG] КТ↔МРТ: t=[${res.params.slice(0, 3).map(v => v.toFixed(1))}] мм, `
    + `r=[${res.params.slice(3).map(v => (v * DEG).toFixed(1))}]°, MI=${res.mi.toFixed(3)}, `
    + `перекрытие ${(res.overlap * 100).toFixed(1)}% (${res.samplesUsed} выборок), `
    + `${((Date.now() - t0) / 1000).toFixed(1)} с (шаг ${res.step}, ${res.samplesPerEval} выборок)`);

  // [7.0.28] Не совмещать «в пустоту». MI≈0 и мизерное перекрытие означают, что
  // объёмы не пересеклись (типично: КТ всего тела и МРТ головы — разные системы
  // координат). Раньше это подавалось как «✓ совмещено» и МРТ сдвигалась в
  // мусорное положение. Теперь честно: том НЕ трогаем, говорим причину.
  if (!res.ok) {
    _state = null;
    log.warn(`[REG] Совмещение не удалось: MI=${res.mi.toFixed(3)}, перекрытие ${(res.overlap * 100).toFixed(1)}%. `
      + 'Объёмы почти не пересекаются в общих координатах.');
    return {
      ok: false, mi: res.mi, overlap: res.overlap, params: res.params,
      fixedId, movingId,
      reason: 'Объёмы почти не пересеклись — вероятно, это КТ и МРТ РАЗНЫХ областей '
        + '(например, КТ всего тела и МРТ головы) или из разных исследований. '
        + 'Загрузите КТ и МРТ одной области, либо совместите вручную стрелками докрутки.',
    };
  }

  // [10.11.0] Отдаём преобразование синхронизации окон.
  //
  // Совмещение до сих пор жило само по себе: считало, двигало том, показывало
  // слой — и ничего не давало сравнению рядом. А ведь именно оно и отвечает на
  // вопрос, из-за которого окна разъезжались: координаты двух сессий
  // отсчитываются от стола, а не от анатомии. Теперь точка из одного
  // исследования переносится в другое целиком, а не поправкой вдоль оси.
  //
  // T переводит точку ОПОРНОГО исследования в координаты ПОДВИЖНОГО — так же,
  // как её применяет расчёт взаимной информации. Обратное направление берём
  // обратной матрицей: у жёсткого преобразования она есть всегда.
  try {
    const T = paramsToMatrix4(res.params, volumeCenterWorld(fixed));
    const обр = _обратная(T);
    const пара = getComparePair();
    const { setCompareTransform, enableCompareSync } = await import('../core/viewportSync.js');
    const { getCompareViewportIds } = await import('../core/compareState.js');
    if (пара && обр) {
      // Порядок важен: включение связи сбрасывает всё прежнее, поэтому сначала
      // включаем окна, и только потом отдаём перенос. Наоборот — и перенос
      // сотрётся в тот же миг.
      const окна = getCompareViewportIds();
      if (окна.length >= 2) enableCompareSync(окна);
      setCompareTransform((точка, откуда, куда, порядок) => {
        // Направление определяем по порядку окон: первое окно — опорное.
        const прямое = Array.isArray(порядок) ? порядок[0] === откуда : true;
        return _применить(прямое ? T : обр, точка);
      });
    }
  } catch (e) { log.warn('[REG] связь с окнами сравнения:', e.message); }

  _state = {
    movingId, fixedId,
    origGeom: orig,
    center: volumeCenterWorld(fixed),
    params: res.params.slice(),
    autoParams: res.params.slice(),   // куда возвращает кнопка «Сброс»
    opacity: opts.opacity ?? 0.5,
    mi: res.mi,
  };

  if (opts.apply !== false) _reapply();

  // [10.13.0] Приводим подвижный том в СИСТЕМУ КООРДИНАТ опорного — формально.
  //
  // Вот почему наложение не рисовалось никогда. Cornerstone отказывается
  // держать в одном окне два тома с разными FrameOfReferenceUID: «Volumes
  // being added to viewport … do not share the same FrameOfReferenceUID. This
  // is not yet supported». Отказ уходил в консоль, исключения не было, актёр
  // молча не добавлялся. Снаружи — «наложение включено» и пустой экран.
  //
  // У двух съёмок разных сессий кадры отсчёта разные по определению. И это
  // правильно ровно до тех пор, пока их не совместили. А совмещение — это и
  // есть утверждение «теперь второй том лежит в координатах первого»: мы
  // только что переставили ему начало и оси именно туда.
  //
  // Поэтому кадр отсчёта переписываем ТОЛЬКО ЗДЕСЬ, после удавшегося
  // совмещения, и только в памяти — файлы на диске не трогаются. Делать это
  // раньше или без совмещения было бы враньём о данных.
  try {
    const мв = _volById(movingId), фв = _volById(fixedId);
    const кадрОпорного = фв?.metadata?.FrameOfReferenceUID;
    if (мв && кадрОпорного) {
      // [10.19.1] Запоминаем только ПЕРВЫЙ, настоящий кадр отсчёта: раньше
      // условие смотрело на несуществующее поле _state.origFrameUID и при
      // повторном совмещении затирало исходный UID уже переписанным.
      if (!_origFrameUID.has(movingId)) _origFrameUID.set(movingId, мв.metadata?.FrameOfReferenceUID || null);
      if (мв.metadata) мв.metadata.FrameOfReferenceUID = кадрОпорного;
      мв.frameOfReferenceUID = кадрОпорного;
      log.info('[REG] подвижный том переведён в кадр отсчёта опорного — наложение станет возможным');
    }
  } catch (e) { log.warn('[REG] кадр отсчёта:', e.message); }

  if (opts.fuse !== false) showFusion();
  return { ok: true, params: res.params, mi: res.mi, overlap: res.overlap, fixedId: _state.fixedId, movingId: _state.movingId,
           matrix: paramsToMatrix4(res.params, _state.center) };
}

/** Пересчитать геометрию подвижного тома из origGeom + текущих params. */
function _reapply() {
  if (!_state) return;
  const mv = _volById(_state.movingId);
  if (!mv) {
    // [10.19.1] Тома нет в кэше — геометрию переписать НЕ УДАЛОСЬ. Раньше это
    // молчаливое «return» оставляло registrationApplied() говорить «переписана»,
    // и карта разницы строилась без матрицы на несовмещённых данных.
    _state.применено = null;
    return;
  }
  const g = applyRigidToGeometry(_state.origGeom, _state.params, _state.center);
  try {
    const img = mv.imageData;
    img?.setOrigin?.(g.origin);
    img?.setDirection?.(g.direction);
    img?.modified?.();
    try { mv.origin = g.origin; mv.direction = g.direction; } catch (e) { /* */ }
    // Отпечаток факта: какой том и какая геометрия ему поставлены. Сверяется в
    // registrationApplied() — намерение и факт больше не одно и то же.
    _state.применено = {
      volumeId: _state.movingId,
      origin: Array.from(g.origin || []),
      direction: g.direction ? Array.from(g.direction) : null,
    };
  } catch (e) {
    _state.применено = null;
    log.warn('[REG] геометрия:', e.message);
  }
  _render();
}

// ── наложение цветным полупрозрачным слоем ──────────────────────────────────

/** Показать подвижный том поверх опорного (цвет + прозрачность). */
export function showFusion(opacity) {
  if (!_state) { log.warn('[REG] Нет совмещения — сначала «Совместить».'); return; }
  if (opacity != null) _state.opacity = opacity;
  const mv = _volById(_state.movingId);
  const range = mv ? _scalarRange(mv) : [0, 1];
  const vps = _mprViewports();
  if (!vps.length) { log.warn('[REG] MPR-окна не найдены.'); return; }
  // [10.8.0] Диапазон слоя — в журнал. От него зависит вся видимость: кривая
  // прозрачности строится по нему, и если диапазон окажется шире настоящего,
  // весь снимок попадёт в первый процент шкалы и слой станет прозрачным
  // целиком. На экране это неотличимо от «слой не добавился».
  log.info(`[REG] слой: диапазон значений [${range[0]}, ${range[1]}], окон ${vps.length}, прозрачность ${_state.opacity}`);

  // Пока слой на экране, страж памяти не должен выбрасывать подвижный том:
  // он не показан «по-настоящему» и без этой просьбы будет сброшен.
  import('../core/viewport.js')
    .then((m) => m.keepVolumeShown?.(_state.movingId))
    .catch(() => { /* без закрепления слой проживёт меньше — но не упадём */ });

  // [10.13.0] Задаём окнам ОБА тома разом — так же, как это делает работающее
  // наложение PET-CT в этой же программе.
  //
  // Опыт показал главное: слой рисуется, но ПОД базовым томом. Спрятал базовый
  // — наложение проступило (яркость 255, пятая часть кадра светлая). Значит
  // дело не в данных, не в кадре отсчёта и не в видимости, а в том, что
  // добавленный отдельно актёр ложится в ту же плоскость и проигрывает.
  //
  // Общая функция setVolumesForViewports собирает набор целиком, и движок
  // строит смешивание сам. Отдельный vp.setVolumes для этого не годится — он
  // отвечал «e.extent is not iterable».
  if (_state.fixedId && typeof cornerstone?.setVolumesForViewports === 'function') {
    const ids = vps.map((v) => v.id);
    const набор = [
      { volumeId: _state.fixedId },
      {
        volumeId: _state.movingId,
        // [10.13.0] Режим смешивания и толщина слоя — то единственное, чем
        // работающее наложение PET-CT в этой же программе отличалось от нашего.
        // Без них слой рисуется, но остаётся под базовым томом: плоскости
        // совпадают, и базовый выигрывает.
        blendMode: cornerstone.Enums?.BlendModes?.MAXIMUM_INTENSITY_BLEND
          || cornerstone.Enums?.BlendModes?.MIP || 'MIP',
        slabThickness: Infinity,
        callback: ({ volumeActor }) => _styleOverlay(volumeActor, range, _state.opacity),
      },
    ];
    cornerstone.setVolumesForViewports(getRenderingEngine(), набор, ids)
      .then(() => {
        for (const vp of _mprViewports()) {
          for (const a of (vp.getActors?.() || [])) {
            if (a.uid && String(a.uid).includes(String(_state.movingId))) _имяСлоя.set(vp.id, a.uid);
          }
        }
        _render();
        log.info(`[REG] Наложение включено набором, прозрачность ${Math.round(_state.opacity * 100)}%`);
      })
      .catch((e) => log.warn('[REG] набор томов не принят:', e.message));
    return;
  }

  for (const vp of vps) {
    try {
      const already = _слойВОкне(vp);
      const былиИмена = new Set((vp.getActors?.() || []).map((a) => a.uid));
      const entry = {
        volumeId: _state.movingId,
        // [10.8.0] Имя актёру задаём САМИ. Без него движок выдаёт случайный
        // uid, и найти свой слой потом нечем: поиск шёл по `a.uid === movingId`
        // и не находил никогда. Наружу это выглядело так, что ползунок
        // прозрачности мёртв, — слой при этом лежал на месте.
        actorUID: _state.movingId,
        callback: ({ volumeActor }) => _styleOverlay(volumeActor, range, _state.opacity),
      };
      // [10.13.0] Кладём ОБА тома разом, а не добавляем один поверх.
      //
      // Прежде звался vp.addVolumes([слой]) — актёр появлялся, том был жив,
      // диапазон значений верный, а на экране не менялось НИЧЕГО: разностная
      // проверка (снимок со слоем и без) давала ноль изменившихся точек.
      //
      // В самой программе двухтомное наложение уже есть и работает — PET-CT в
      // DatasetManager. Оно устроено иначе: не добавляет второй том к первому,
      // а задаёт окну СПИСОК из двух. Разница не косметическая: движок
      // собирает смешивание, когда получает набор целиком.
      // [10.13.0] Добавляем слой к тому, что уже в окне.
      //
      // Пробовал задавать окну список из двух томов разом (так устроено PET-CT
      // в этой же программе) — движок отвечал «e.extent is not iterable»:
      // пересобирать базовый том на ходу он не готов. И это оказалось лечением
      // не той болезни: настоящая причина была в кадре отсчёта, и как только
      // подвижный том переведён в кадр опорного, обычное добавление работает.
      if (!already && vp.addVolumes) {
        vp.addVolumes([entry], true);
      } else { _styleOverlayInViewport(vp, range); }

      // Имя нового актёра запоминаем: движок именует их случайно и наше имя
      // игнорирует, а без имени слой потом не найти ни для прозрачности, ни
      // для снятия.
      if (!already) {
        for (const a of (vp.getActors?.() || [])) {
          if (!былиИмена.has(a.uid)) { _имяСлоя.set(vp.id, a.uid); break; }
        }
      }
    } catch (e) { log.warn('[REG] наложение в окне:', e.message); }
  }
  _render();
  log.info(`[REG] Наложение включено, прозрачность ${Math.round(_state.opacity * 100)}%`);
}

function _styleOverlay(volumeActor, range, opacity) {
  try {
    // [10.13.0] Включаем видимость явно.
    //
    // Щуп показал: слой найден, кривые цвета и прозрачности применены, ошибок
    // нет — а `getVisibility()` у актёра `false`. Движок заводит добавленный
    // том невидимым и включает его сам не всегда; для нас «не всегда» означает
    // «никогда»: наложение не рисовалось ни разу. Спрашивать, почему так
    // устроено, дешевле, чем полагаться на догадку, — просто говорим прямо.
    volumeActor.setVisibility?.(true);
    // [7.0.23] Точки функций — из fusionStyleCore (под тестами, с защитой от
    // вырожденного диапазона «плоского» тома); здесь только вызовы vtk.
    const prop = volumeActor.getProperty();
    const ctf = prop.getRGBTransferFunction(0);
    ctf.removeAllPoints();
    for (const [x, r, g, b] of fusionColorPoints(range)) ctf.addRGBPoint(x, r, g, b);
    const ofun = prop.getScalarOpacity(0);
    ofun.removeAllPoints();
    for (const [x, op] of fusionOpacityPoints(range, opacity)) ofun.addPoint(x, op);
    prop.setInterpolationTypeToLinear?.();
  } catch (e) { log.warn('[REG] стиль слоя:', e.message); }
}
function _styleOverlayInViewport(vp, range) {
  for (const a of (vp.getActors?.() || [])) {
    if (_этоНашСлой(a, vp.id) && a.actor) _styleOverlay(a.actor, range, _state.opacity);
  }
}

/**
 * Опознать свой слой среди актёров окна.
 *
 * [10.8.0] Сначала здесь сравнивались имена: `a.uid === movingId`. Не
 * работало никогда — этот движок именует актёров случайными строками вида
 * «d-445a-b922-…», и просьба назвать актёра своим именем (actorUID) ничего не
 * меняет. Поэтому имя не угадываем, а ЗАПОМИНАЕМ: перед добавлением слоя
 * переписываем имена актёров окна, после добавления новое имя и есть наше.
 */
const _имяСлоя = new Map();   // vpId → uid актёра наложения

function _этоНашСлой(a, vpId) {
  if (!a || !_state) return false;
  if (vpId && _имяСлоя.get(vpId) === a.uid) return true;
  return a.uid === _state.movingId
    || a.referencedId === _state.movingId
    || a.referenceId === _state.movingId
    || a.volumeId === _state.movingId;
}

/** Лежит ли наш слой в этом окне. */
function _слойВОкне(vp) {
  try { return (vp.getActors?.() || []).some((a) => _этоНашСлой(a, vp.id)); } catch (e) { return false; }
}

/**
 * В скольких окнах слой действительно лежит.
 *
 * «Позвали showFusion» и «слой на экране» — разные вещи. Путь реформатов
 * доустанавливает тома асинхронно, и его последний setVolumesForViewports
 * заменяет всех актёров окна разом, снося и наш слой. Ошибки при этом нет:
 * журнал говорит «наложение включено», а на экране его нет. Проверять надо
 * фактом, а не намерением.
 */
export function fusionCoverage() {
  if (!_state) return { total: 0, with: 0 };
  const vps = _mprViewports();
  return { total: vps.length, with: vps.filter(_слойВОкне).length };
}

/**
 * [10.13.0] Спросить у слоя, что с ним на самом деле.
 *
 * «Актёр добавлен» и «слой виден» — разные вещи, а между ними лежит десяток
 * причин: не применилась кривая прозрачности, актёр невидим, у тома нет
 * данных, движок не собрал шейдер. Отличить их снаружи нечем, а консоли на
 * телефоне нет. Поэтому спрашиваем прямо.
 */
export function fusionProbe() {
  const out = [];
  if (!_state) return out;
  for (const vp of _mprViewports()) {
    const запись = { окно: vp.id, актёров: (vp.getActors?.() || []).length };
    for (const a of (vp.getActors?.() || [])) {
      if (!_этоНашСлой(a, vp.id) || !a.actor) continue;
      запись.слойНайден = true;
      try {
        const prop = a.actor.getProperty();
        const of = prop.getScalarOpacity(0);
        const ctf = prop.getRGBTransferFunction(0);
        запись.точекПрозрачности = of?.getSize?.() ?? null;
        запись.точекЦвета = ctf?.getSize?.() ?? null;
        запись.прозрачностьВСередине = of?.getValue?.(300) ?? null;
        запись.видимость = a.actor.getVisibility?.();
      } catch (e) { запись.ошибка = e.message; }
      try {
        const мв = _volById(_state.movingId);
        const r = мв?.imageData?.getPointData?.()?.getScalars?.()?.getRange?.();
        запись.диапазонТома = r ? [r[0], r[1]] : null;
        запись.размерТома = мв?.dimensions || null;
        запись.кадрОтсчёта = мв?.metadata?.FrameOfReferenceUID || null;
      } catch (e) { /* */ }
    }
    out.push(запись);
  }
  return out;
}

/**
 * [10.13.0] Показать одно исследование или другое в тех же окнах — мигание.
 *
 * Смешать два тома цветом в этой версии движка не удалось: слой рисуется, но
 * остаётся под базовым, плоскости совпадают и базовый выигрывает. Это доказано
 * опытом — стоит спрятать базовый том, и наложение проступает.
 *
 * Отсюда вышла вещь полезнее самого смешивания. Мигание — переключение между
 * двумя съёмками в ОДНОМ окне на одном уровне — давняя рабочая привычка
 * лучевой диагностики: глаз замечает изменение по «прыжку» лучше, чем по
 * полупрозрачному цвету. А после совмещения оба тома стоят в одних
 * координатах, значит прыгать будет только то, что действительно изменилось.
 *
 * @param {'fixed'|'moving'|'both'} что
 */
export function showFusionLayer(что) {
  if (!_state) return { ok: false };
  let база = 0, слой = 0;
  for (const vp of _mprViewports()) {
    for (const a of (vp.getActors?.() || [])) {
      if (!a.actor?.setVisibility) continue;
      const наш = _этоНашСлой(a, vp.id);
      const видно = что === 'both' ? true : (наш ? что === 'moving' : что === 'fixed');
      a.actor.setVisibility(видно);
      if (наш) слой += видно ? 1 : 0; else база += видно ? 1 : 0;
    }
    try { vp.render?.(); } catch (e) { /* */ }
  }
  _state.показ = что;
  _render();
  log.info(`[REG] показ: ${что} (базовых актёров видно ${база}, слоёв ${слой})`);
  return { ok: true, что, база, слой };
}

/** Что сейчас показано. */
export const currentFusionLayer = () => _state?.показ || 'both';

/**
 * [10.14.0] Измерить очаг в конкретной серии от точки в координатах пациента.
 *
 * Живёт здесь, потому что здесь доступ к томам. Считает не этот модуль, а
 * чистый lesionVolume — его можно проверить арифметикой на шаре известного
 * радиуса, что и сделано. Тут только достать воксели и перевести точку из
 * миллиметров в индексы.
 *
 * @param {string} seriesUID @param {number[]} мир — [x,y,z] мм
 * @param {{максМм?: number, доляОтСемени?: number}} [o]
 */
export async function measureLesionInSeries(seriesUID, мир, o = {}) {
  if (!seriesUID || !Array.isArray(мир)) return { ok: false, why: 'нет серии или точки' };
  const полное = `cornerstoneStreamingImageVolume:volume_${seriesUID}`;
  let csVol = _volById(полное) || _volById(`volume_${seriesUID}`);

  if (!csVol) {
    // Тома ещё нет — в сравнении окна работают на плоских стопках. Собираем.
    try {
      const { ensureVolumeReady } = await import('../core/viewport.js');
      const { getWorkerSeriesImageIds } = await import('../loaders/loaderWorkerBridge.js');
      let кадры = getWorkerSeriesImageIds(seriesUID) || [];
      if (!кадры.length) {
        // [10.19.1] Серии старого загрузчика (без воркера) видны в окнах, но
        // мостом не числятся — измерение отвечало «у серии нет кадров».
        try {
          const лм = await import('../loaders/loaderManager.js');
          кадры = лм.getSeriesImageIds?.(seriesUID) || [];
        } catch (e) { /* */ }
      }
      if (!кадры.length) return { ok: false, why: 'у серии нет кадров' };
      const id = await ensureVolumeReady(`volume_${seriesUID}`, кадры);
      csVol = id ? _volById(id) : null;
    } catch (e) { return { ok: false, why: e.message }; }
  }
  if (!csVol) return { ok: false, why: 'объём не собрался' };

  const том = extractVolume(csVol, 1, { noCopy: true });
  if (!том) return { ok: false, why: 'нет доступа к вокселям' };

  // [10.19.1] Точка приходит в координатах СТОПКИ (исходный DICOM), а
  // геометрия тома после совмещения ПЕРЕПИСАНА в кадр опорного (_reapply).
  // Перевод точки по переписанной геометрии молча уводил семя на всю величину
  // совмещения — только для подвижной серии и только после «Совместить»:
  // клик в одном окне давал верное число, в другом — правдоподобное чужое.
  // Меряем в исходной геометрии стопки: пока совмещения нет, она совпадает с
  // текущей; после — остаётся верной для точки из окна-стопки. Воксели те же,
  // _reapply меняет только origin/direction.
  try {
    const vid = csVol.volumeId || csVol.id;
    const исходная = _pristineGeom(csVol, vid);
    if (исходная?.origin) том.origin = исходная.origin;
    if (исходная?.direction) { том.direction = исходная.direction; delete том._обр; }
  } catch (e) { /* без исходной геометрии считаем по текущей, как раньше */ }

  const idx = worldToIdx(том, мир);
  const и = growLesion(том, idx, o);
  if (!и) {
    return { ok: false,
      why: `точка вне объёма: мир [${мир.map((v) => v.toFixed(1)).join(', ')}] → индекс `
        + `[${idx.map((v) => (Number.isFinite(v) ? v.toFixed(1) : v)).join(', ')}], `
        + `размер [${том.dims.join('×')}], начало [${(том.origin || []).map((v) => v.toFixed(1)).join(', ')}], `
        + `шаг [${(том.spacing || []).join(', ')}]` };
  }
  return { ok: true, seriesUID, объёмМм3: и.объёмМм3, вокселей: и.вокселей,
           среднее: и.среднее, габарит: и.габарит, упёрлись: и.упёрлись };
}

/** Убрать наложение (том остаётся загружен). */
export function removeFusion() {
  if (!_state) return;
  for (const vp of _mprViewports()) {
    // Убираем по ЗАПОМНЕННОМУ имени: движок дал актёру своё, случайное, и по
    // movingId его не найти — снятие слоя раньше просто ничего не делало.
    const имя = _имяСлоя.get(vp.id);
    try { vp.removeVolumeActors?.([имя || _state.movingId], true); } catch (e) { /* */ }
    _имяСлоя.delete(vp.id);
  }
  // Слоя нет — держать подвижный том в памяти больше незачем.
  import('../core/viewport.js')
    .then((m) => m.releaseVolumeShown?.(_state.movingId))
    .catch(() => { /* */ });
  _render();
  log.info('[REG] Наложение убрано.');
}

/** Прозрачность наложения 0..1. */
export function setOverlayOpacity(v) {
  if (!_state) return;
  _state.opacity = Math.max(0, Math.min(1, v));
  const mv = _volById(_state.movingId);
  const range = mv ? _scalarRange(mv) : [0, 1];
  for (const vp of _mprViewports()) _styleOverlayInViewport(vp, range);
  _render();
}

// ── ручная докрутка (правим позу и пересчитываем геометрию) ─────────────────

export function nudgeTranslate(axis, mm) {
  if (!_state) return; const i = { x: 0, y: 1, z: 2 }[axis]; if (i == null) return;
  _state.params[i] += mm; _reapply();
}
export function nudgeRotate(axis, deg) {
  if (!_state) return; const i = { x: 3, y: 4, z: 5 }[axis]; if (i == null) return;
  _state.params[i] += deg * Math.PI / 180; _reapply();
}
/**
 * Сброс ручной докрутки — к АВТОМАТИЧЕСКИ найденному совмещению.
 * [6.7.37] Раньше эта кнопка обнуляла позу, то есть выбрасывала результат
 * совмещения целиком: подписана «Сброс», а на деле снимала всю работу, и
 * вернуть её можно было только повторным расчётом. Теперь «Сброс» возвращает
 * к автоматическому результату, а снять совмещение полностью — отдельная кнопка.
 */
export function resetAdjust() {
  if (!_state) return;
  _state.params = (_state.autoParams || [0, 0, 0, 0, 0, 0]).slice();
  _reapply();
  log.info('[REG] Ручная докрутка сброшена к автоматическому совмещению.');
}

/** Полностью снять совмещение: вернуть подвижный том в положение «как загружен». */
export function clearRegistration() {
  if (!_state) return;
  _state.params = [0, 0, 0, 0, 0, 0];
  _reapply();
  // [10.19.1] Вернуть подвижному тому исходный кадр отсчёта: registerCtMr
  // переписал его на кадр опорного ради наложения, и раньше это никто не
  // откатывал.
  try {
    const мв = _volById(_state.movingId);
    const исходный = _origFrameUID.get(_state.movingId);
    if (мв && исходный) {
      if (мв.metadata) мв.metadata.FrameOfReferenceUID = исходный;
      мв.frameOfReferenceUID = исходный;
    }
  } catch (e) { /* */ }
  // [10.19.1] Снятое совмещение — это ОТСУТСТВИЕ совмещения, а не совмещение
  // с нулевой позой. Раньше _state жил дальше: currentMatrix() возвращала
  // единичную матрицу вместо null, статус карты лгал «по совмещению», а
  // предупреждение «совмещение не выполнено» подавлялось.
  _state = null;
  log.info('[REG] Совмещение снято — МРТ вернулась в исходное положение.');
}
/** Текущая матрица совмещения (для сохранения). */
export function currentMatrix() {
  return _state ? paramsToMatrix4(_state.params, _state.center) : null;
}

/** Применить матрицу 4×4 к точке. */
function _применить(M, p) {
  return [
    M[0] * p[0] + M[1] * p[1] + M[2] * p[2] + M[3],
    M[4] * p[0] + M[5] * p[1] + M[6] * p[2] + M[7],
    M[8] * p[0] + M[9] * p[1] + M[10] * p[2] + M[11],
  ];
}

/**
 * Обратная к жёсткому преобразованию: поворот транспонируем, сдвиг переносим.
 * Общая формула обращения 4×4 здесь не нужна и была бы лишним местом для
 * ошибки: у жёсткого преобразования поворот ортогонален по построению.
 */
export function inverseMatrix(M) { return _обратная(M); }

/**
 * [10.16.0] Переписана ли уже геометрия подвижного тома.
 *
 * Вопрос не праздный. _reapply() ставит тому новые origin и direction сразу
 * после удавшегося совмещения — то есть том УЖЕ лежит в координатах опорного.
 * Тот, кто возьмёт его воксели и применит к ним ещё и матрицу, сдвинет данные
 * вдвое. Проверено дорого: карта разницы так и делала и насчитала 41 мл
 * «прибавки» и 61 мл «убыли» там, где на самом деле выросло 2,5 мл. Ничего не
 * падало, в журнале было чисто.
 */
export function registrationApplied(volumeId) {
  const п = _state?.применено;
  if (!п) return false;
  if (volumeId && п.volumeId !== volumeId) return false;
  // [10.19.1] Намерению больше не верим — сверяем ФАКТ с живым томом.
  // Страж памяти мог выгрузить подвижный том, а ensureVolumeReady — собрать
  // его заново с исходной DICOM-геометрией; registerCtMr({apply:false}) вообще
  // ничего не переписывает. Во всех этих случаях прежний ответ «переписана»
  // был ложью, и карта строилась без матрицы на несовмещённых данных.
  const mv = _volById(п.volumeId);
  if (!mv) return false;
  const g = _geomOf(mv);
  if (!g?.origin || п.origin.length !== g.origin.length) return false;
  for (let i = 0; i < 3; i++) if (Math.abs(g.origin[i] - п.origin[i]) > 1e-3) return false;
  if (п.direction) {
    if (!g.direction || g.direction.length !== 9) return false;
    for (let i = 0; i < 9; i++) if (Math.abs(g.direction[i] - п.direction[i]) > 1e-6) return false;
  }
  return true;
}

/**
 * [10.19.1] Сводка о текущем совмещении — наружу, чтобы карта разницы могла
 * сверить, к ТОЙ ли паре серий оно относится, а проверки — увидеть параметры.
 */
export function registrationInfo() {
  if (!_state) return null;
  const mv = _volById(_state.movingId), fx = _state.fixedId ? _volById(_state.fixedId) : null;
  return {
    fixedId: _state.fixedId || null,
    movingId: _state.movingId,
    fixedSeries: fx ? _seriesUidOf(fx) : null,
    movingSeries: mv ? _seriesUidOf(mv) : null,
    params: _state.params.slice(),
    applied: registrationApplied(_state.movingId),
  };
}

function _обратная(M) {
  if (!M || M.length < 12) return null;
  const R = [M[0], M[1], M[2], M[4], M[5], M[6], M[8], M[9], M[10]];
  const t = [M[3], M[7], M[11]];
  const Rt = [R[0], R[3], R[6], R[1], R[4], R[7], R[2], R[5], R[8]];   // транспонированный
  const nt = [
    -(Rt[0] * t[0] + Rt[1] * t[1] + Rt[2] * t[2]),
    -(Rt[3] * t[0] + Rt[4] * t[1] + Rt[5] * t[2]),
    -(Rt[6] * t[0] + Rt[7] * t[1] + Rt[8] * t[2]),
  ];
  return [Rt[0], Rt[1], Rt[2], nt[0], Rt[3], Rt[4], Rt[5], nt[1], Rt[6], Rt[7], Rt[8], nt[2], 0, 0, 0, 1];
}

function _render() { try { getRenderingEngine()?.render?.(); } catch (e) { /* */ } }

// ── сохранение / загрузка совмещения ────────────────────────────────────────

/** Сохранить текущее совмещение в JSON-файл (привязка по SeriesInstanceUID). */
export function saveRegistration() {
  if (!_state) { log.warn('[REG] Нет совмещения для сохранения.'); return null; }
  const mv = _volById(_state.movingId), fx = _volById(_state.fixedId);
  const obj = {
    format: 'mikhmel-registration', version: 1,
    fixedSeries: fx ? _seriesUidOf(fx) : null,
    movingSeries: mv ? _seriesUidOf(mv) : null,
    center: _state.center, params: _state.params,
    matrix: paramsToMatrix4(_state.params, _state.center),
  };
  try {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'registration_ct_mr.json';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    log.info('[REG] Совмещение сохранено в файл.');
  } catch (e) { log.warn('[REG] сохранение:', e.message); }
  return obj;
}

/** Загрузить совмещение из объекта JSON и применить к загруженным томам. */
export function loadRegistration(obj) {
  if (!obj || obj.format !== 'mikhmel-registration' || !Array.isArray(obj.params) || obj.params.length !== 6) {
    log.warn('[REG] Неверный файл совмещения.'); return false;
  }
  const vols = _loadedVolumes();
  const moving = (obj.movingSeries && vols.find(v => _seriesUidOf(v) === obj.movingSeries)) || null;
  const fixed = (obj.fixedSeries && vols.find(v => _seriesUidOf(v) === obj.fixedSeries)) || null;
  if (!moving) { log.warn('[REG] Подвижный том (та же серия МРТ) не загружен.'); return false; }
  const movingId = moving.volumeId || moving.id;
  // Именно ИСХОДНАЯ геометрия, а не текущая — иначе преобразование ляжет вторым слоем.
  const orig = _pristineGeom(moving, movingId);
  _state = {
    movingId,
    fixedId: fixed ? (fixed.volumeId || fixed.id) : null,
    origGeom: orig,
    center: Array.isArray(obj.center) ? obj.center.slice() : orig.origin.slice(),
    params: obj.params.slice(),
    autoParams: obj.params.slice(),
    opacity: 0.5,
  };
  _reapply();
  showFusion();
  log.info('[REG] Совмещение загружено из файла и применено.');
  return true;
}

// ── [10.8.0] Совмещение пары, выбранной в разделе «Сравнение» ───────────────
//
// Сравнение работает на СТОПКАХ срезов: их хватает, чтобы листать рядом, и они
// вдвое дешевле по памяти. А совмещению нужны ОБЪЁМЫ, и оба сразу: взаимная
// информация считается по вокселям. Поэтому объёмы здесь сначала собираются,
// это занимает время, и молчать в это время нельзя — отсюда onStage.

/**
 * Собрать объёмы выбранной пары, открыть опорную серию в реформатах и
 * совместить. Пары нет — прежнее поведение (два тома из кэша).
 *
 * @param {{onStage?: (текст: string) => void}} [opts]
 */
export async function registerComparePair(opts = {}) {
  const пара = getComparePair();
  const скажем = (t) => { try { opts.onStage?.(t); } catch (e) { /* */ } };

  if (!пара) return registerCtMr(opts);

  const { ensureVolumeReady } = await import('../core/viewport.js');
  const api = (typeof window !== 'undefined') ? window.MikhmelViewer : null;

  скажем('Собираю объём опорного исследования…');
  // [10.8.0] Сколько кадров нам ДАЛИ — отдельно от того, сколько попало в том.
  // Расхождение между этими числами и есть место поломки.
  log.info(`[REG] пара: опорная серия ${пара.fixedIds.length} кадров, подвижная ${пара.movingIds.length}`);
  const fixedId = await ensureVolumeReady(`volume_${пара.fixedSeriesUID}`, пара.fixedIds);
  скажем('Собираю объём второго исследования…');
  const movingId = await ensureVolumeReady(`volume_${пара.movingSeriesUID}`, пара.movingIds);

  if (!fixedId || !movingId) {
    return { ok: false, mi: 0, overlap: 0, params: [0, 0, 0, 0, 0, 0],
      reason: 'Не удалось собрать объёмы из выбранных серий. Нужны серии от десяти срезов с геометрией среза.' };
  }

  // Реформаты строим на ОПОРНОЙ серии: наложение ложится поверх неё.
  скажем('Открываю реформаты по опорному исследованию…');
  try {
    await api?.changeLayout?.('mpr');
    await api?.loadSeriesIntoViewport?.(пара.fixedSeriesUID);
  } catch (e) { log.warn('[REG] реформаты:', e.message); }

  скажем('Считаю совмещение…');
  const r = await registerCtMr({ ...opts, fixedId, movingId });
  if (!r || r.ok === false) return r;

  // [10.8.0] Убедиться, что слой ДЕЙСТВИТЕЛЬНО лёг, а не «был включён».
  // Проверено вживую: сразу после «наложение включено» в окнах оставалось по
  // одному актёру вместо двух — реформаты доустанавливали тома и сносили слой.
  for (let попытка = 1; попытка <= 3; попытка++) {
    await new Promise((r2) => setTimeout(r2, 700));
    const п = fusionCoverage();
    if (п.total && п.with >= п.total) break;
    скажем(`Кладу слой поверх реформатов (${п.with}/${п.total})…`);
    try { showFusion(); } catch (e) { log.warn('[REG] повтор наложения:', e.message); }
  }
  const итог = fusionCoverage();
  if (!итог.total || итог.with < итог.total) {
    log.warn(`[REG] Слой лёг не во всех окнах: ${итог.with} из ${итог.total}.`);
  }
  return { ...r, fusion: итог };
}

// ── UI ──────────────────────────────────────────────────────────────────────

/**
 * Показать в панели, какая пара будет совмещаться.
 *
 * Раньше панель молчала о том, что возьмёт, — а брала «два самых больших тома
 * в кэше». Врач нажимал «Совместить» и получал результат для пары, которую не
 * выбирал. Здесь это названо прямо, до нажатия.
 */
export function refreshRegisterPair() {
  const el = document.getElementById('reg-pair');
  if (!el) return;
  const п = getComparePair();
  if (!п) {
    el.innerHTML = 'Пара не выбрана. Откройте раздел <b>«Сравнение»</b>, положите исследования в оба места и нажмите «Сравнить» — сюда попадёт та же пара.';
    return;
  }
  el.innerHTML = `Опорное (неподвижно): <b>${п.fixedLabel || 'место 1'}</b><br>`
    + `Подвижное (подгоняется): <b>${п.movingLabel || 'место 2'}</b>`;
}

export function mountRegisterUI() {
  if (document.getElementById('reg-ctmr-box')) return;
  // [10.8.0] Панель искала контейнер `rtstruct-panel`, которого в разметке нет
  // ВООБЩЕ. При его отсутствии она падала голым блоком в конец страницы: код
  // отрабатывал, журнал молчал, а до врача панель не доходила никогда. Это тот
  // самый случай, когда «написано» и «работает» — разные вещи.
  const host = document.getElementById('register-panel')
    || document.getElementById('rtstruct-panel')
    || document.body;
  const box = document.createElement('div');
  box.id = 'reg-ctmr-box';
  box.style.cssText = 'margin:8px;padding:8px;border:1px solid #2a4;border-radius:8px;'
    + 'background:#0d1420;color:#cfe;font:12px sans-serif;max-width:280px';
  box.innerHTML = `
    <div id="reg-pair" style="font-size:11px;line-height:1.45;margin-bottom:6px;color:#8ab"></div>
    <label style="display:flex;align-items:center;gap:6px;font-size:11px;margin-bottom:4px;cursor:pointer" title="Если опорная съёмка шире головы (КТ всего тела, грудной клетки): наведите перекрестье на голову и включите — совмещение пойдёт по вырезанной голове.">
      <input type="checkbox" id="reg-crop"> опорная съёмка шире головы — обрезать вокруг перекрестья
    </label>
    <label id="reg-crop-half-row" style="display:flex;align-items:center;gap:6px;font-size:11px;margin-bottom:6px">
      полразмера бокса, мм <input id="reg-crop-half" type="number" value="110" min="40" max="200" style="width:56px">
    </label>
    <button id="reg-cuthead" style="width:100%;margin-bottom:6px;padding:6px;border-radius:6px;border:1px solid #58a;background:#0f1a2a;color:#bdf;cursor:pointer;font-size:12px" title="Вырезать голову из КТ вокруг перекрестья в ОТДЕЛЬНУЮ серию — её видно во всех окнах, можно листать и сохранить как DICOM">✂ Вырезать голову в новую серию</button>
    <button id="reg-run" style="width:100%;padding:8px;border-radius:6px;border:1px solid #3a6;background:#123;color:#cfe;cursor:pointer">🧲 Совместить</button>
    <div id="reg-hint" style="display:none;margin-top:6px;padding:6px 8px;border:1px solid #a55;border-radius:6px;background:#2a1414;color:#fcc;font-size:11px;line-height:1.4"></div>
    <div id="reg-controls" style="display:none;margin-top:8px">
      <label style="display:block;margin:6px 0 2px">Прозрачность наложения: <span id="reg-op-val">50%</span></label>
      <input id="reg-op" type="range" min="0" max="100" value="50" style="width:100%">
      <div style="margin-top:8px">Показывать:</div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:4px;margin-top:4px">
        <button data-показ="fixed" title="Только первое исследование">Первое</button>
        <button data-показ="moving" title="Только второе исследование">Второе</button>
        <button data-показ="both" title="Оба слоя сразу">Оба</button>
      </div>
      <div style="margin-top:4px;color:#8ab;font-size:10px">Переключайте «Первое ⇄ Второе» — изменение видно по прыжку.</div>
      <div style="margin-top:6px">Ручная докрутка:</div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:4px;margin-top:4px">
        <button data-nt="x" data-d="-1">◀ X</button><button data-nt="x" data-d="1">X ▶</button><button data-nr="x" data-d="-1">⟲ X</button>
        <button data-nt="y" data-d="-1">◀ Y</button><button data-nt="y" data-d="1">Y ▶</button><button data-nr="y" data-d="-1">⟲ Y</button>
        <button data-nt="z" data-d="-1">◀ Z</button><button data-nt="z" data-d="1">Z ▶</button><button data-nr="z" data-d="-1">⟲ Z</button>
      </div>
      <div style="display:flex;gap:4px;margin-top:6px">
        <button id="reg-toggle" style="flex:1">Скрыть слой</button>
        <button id="reg-reset" style="flex:1" title="Отменить ручную докрутку, вернуться к автоматическому результату">Сброс докрутки</button>
      </div>
      <button id="reg-clear" style="width:100%;margin-top:4px" title="Вернуть МРТ в положение, в котором она была загружена">Снять совмещение</button>
      <div style="display:flex;gap:4px;margin-top:4px">
        <button id="reg-save" style="flex:1">💾 Сохранить</button>
        <button id="reg-load" style="flex:1">📂 Загрузить</button>
        <input id="reg-file" type="file" accept="application/json,.json" style="display:none">
      </div>
      <div style="margin-top:4px;color:#8ab">шаг: сдвиг 1 мм, поворот 1°</div>
    </div>`;
  for (const b of box.querySelectorAll('button')) {
    b.style.cssText = (b.style.cssText || '') + ';padding:5px;border-radius:5px;border:1px solid #345;background:#16202e;color:#cfe;cursor:pointer;font-size:11px';
  }
  host.appendChild(box);

  const run = box.querySelector('#reg-run');
  const ctrls = box.querySelector('#reg-controls');
  const cropCb = box.querySelector('#reg-crop');
  refreshRegisterPair();

  const cut = box.querySelector('#reg-cuthead');
  cut.onclick = async () => {
    const old = cut.textContent; cut.disabled = true; cut.textContent = '⏳ Вырезаю КТ головы…';
    await new Promise(r => setTimeout(r, 50));
    try {
      const half = +box.querySelector('#reg-crop-half').value || 110;
      const r = await extractHeadCT({ halfMm: half });
      cut.textContent = `✅ КТ головы: ${r.slices} срезов — выберите в списке серий`;
      const hint = box.querySelector('#reg-hint');
      if (hint) { hint.style.display = 'block'; hint.style.borderColor = '#3a6'; hint.style.background = '#0d1f14'; hint.style.color = '#bfe';
        hint.textContent = `Новая серия «КТ головы» (${r.slices} срезов, оставлено ${(r.keptFraction * 100).toFixed(0)}%) добавлена в список серий. Выберите её как опорную и жмите «Совместить», либо сохраните её через Экспорт.`; }
    } catch (e) { cut.textContent = '❌ ' + e.message; log.error('[REG] cut head:', e); }
    finally { setTimeout(() => { cut.disabled = false; cut.textContent = old; }, 3000); }
  };
  run.onclick = async () => {
    run.disabled = true;
    run.textContent = '⏳ Считаю совмещение (несколько секунд)…';
    // Расчёт синхронный и держит поток: даём браузеру кадр, чтобы надпись
    // успела нарисоваться, иначе кнопка «замирает» без единого признака работы.
    await new Promise(r => setTimeout(r, 50));
    try {
      const opts = {};
      if (cropCb.checked) {
        const c = _focalPointWorld();
        if (!c) { run.textContent = '⚠️ Нет перекрестья — наведите на голову в КТ'; run.disabled = false; return; }
        opts.cropFixedTo = { center: c, halfMm: Math.max(40, Math.min(200, +box.querySelector('#reg-crop-half').value || 110)) };
      }
      // [10.8.0] Через registerComparePair: он соберёт объёмы выбранной пары,
      // откроет реформаты по опорной серии и только потом посчитает.
      opts.onStage = (t) => { run.textContent = '⏳ ' + t; };
      const r = await registerComparePair(opts);
      if (!r) { run.textContent = '⚠️ Нужны два исследования — выберите их в разделе «Сравнение»'; }
      else if (r.ok === false) {
        // Честно: не совместилось. Показываем причину и НЕ открываем панель
        // наложения (двигать нечего). Ручная докрутка доступна отдельно.
        run.textContent = `❌ Не удалось: MI=${r.mi.toFixed(2)}, перекрытие ${(r.overlap * 100).toFixed(1)}%`;
        const hint = box.querySelector('#reg-hint');
        if (hint) { hint.textContent = r.reason || 'Объёмы не пересеклись.'; hint.style.display = 'block'; }
        else log.warn('[REG] ' + (r.reason || ''));
      }
      else {
        // [10.8.0] Показываем НАЙДЕННОЕ СМЕЩЕНИЕ, а не только MI. По MI человеку
        // судить не о чем: 0,9 выглядит хорошо и при неверном ответе. А «сдвиг
        // 6 мм, поворот 2°» врач оценит сразу — голова между съёмками так и
        // лежит. Скажет программа «сдвиг 60 мм» — это повод ей не поверить, и
        // повод надо дать увидеть.
        const Г = 180 / Math.PI;
        const сдвиг = Math.hypot(r.params[0], r.params[1], r.params[2]);
        const поворот = Math.max(...r.params.slice(3).map((v) => Math.abs(v * Г)));
        run.textContent = `✓ сдвиг ${сдвиг.toFixed(1)} мм, поворот ${поворот.toFixed(1)}° · MI=${r.mi.toFixed(2)}, перекрытие ${(r.overlap * 100).toFixed(0)}%`;
        const hint = box.querySelector('#reg-hint');
        const п = r.fusion;
        if (hint) {
          if (п && п.total && п.with < п.total) {
            hint.textContent = `Слой лёг не во всех проекциях (${п.with} из ${п.total}). Нажмите «Показать слой» ещё раз.`;
            hint.style.display = 'block';
          } else hint.style.display = 'none';
        }
        ctrls.style.display = 'block';
      }
    } catch (e) { run.textContent = '❌ ' + e.message; log.error('[REG]', e); }
    finally { setTimeout(() => { run.disabled = false; }, 500); }
  };
  const op = box.querySelector('#reg-op'), opVal = box.querySelector('#reg-op-val');
  op.oninput = () => { opVal.textContent = op.value + '%'; setOverlayOpacity(op.value / 100); };
  box.querySelectorAll('[data-показ]').forEach(b => b.onclick = () => showFusionLayer(b.dataset.показ));
  box.querySelectorAll('[data-nt]').forEach(b => b.onclick = () => nudgeTranslate(b.dataset.nt, +b.dataset.d));
  box.querySelectorAll('[data-nr]').forEach(b => b.onclick = () => nudgeRotate(b.dataset.nr, +b.dataset.d));
  let shown = true;
  box.querySelector('#reg-toggle').onclick = (e) => {
    shown = !shown; if (shown) { showFusion(); e.target.textContent = 'Скрыть слой'; }
    else { removeFusion(); e.target.textContent = 'Показать слой'; }
  };
  box.querySelector('#reg-reset').onclick = () => resetAdjust();
  box.querySelector('#reg-clear').onclick = () => clearRegistration();

  box.querySelector('#reg-save').onclick = () => saveRegistration();
  const fileInput = box.querySelector('#reg-file');
  box.querySelector('#reg-load').onclick = () => fileInput.click();
  fileInput.onchange = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    try {
      const obj = JSON.parse(await f.text());
      const okLoad = loadRegistration(obj);
      if (okLoad) ctrls.style.display = 'block';
      else run.textContent = '⚠️ Не удалось загрузить совмещение';
    } catch (err) { log.error('[REG] загрузка файла:', err); }
    finally { fileInput.value = ''; }
  };
}

export function initRegistration() {
  try {
    mountRegisterUI();
    registerAPIs({
      registerCtMr, extractVolume, showFusion, removeFusion, extractHeadCT, fusionProbe,
      showFusionLayer, currentFusionLayer, measureLesionInSeries,
      setOverlayOpacity, nudgeTranslate, nudgeRotate, resetAdjust, clearRegistration,
      currentMatrix, saveRegistration, loadRegistration,
    });
    log.info('[REG] Модуль регистрации КТ↔МРТ — 1.3 (Этап 3: наложение + докрутка + сохранение)');
  } catch (e) { log.error('[REG] init failed:', e); }
}

export default { initRegistration, registerCtMr, extractVolume, showFusion, removeFusion, setOverlayOpacity, nudgeTranslate, nudgeRotate, resetAdjust, clearRegistration, currentMatrix, saveRegistration, loadRegistration };
